import { useEffect, useState } from 'react';
import { Box, Button, Typography, Link as MuiLink, useTheme } from '@mui/material';
import { TYPE } from '@/theme/typography';
import { COOKIE_CONSENT_COLORS } from '@/theme/colorTokens';
import { getConsent, setConsent } from '@/lib/cookieConsent';

/**
 * NDPR/POPIA/GDPR-compliant consent bar. Shows once — decision is stored in
 * localStorage. Users can re-open it later from Settings → Privacy (future).
 *
 * Essential cookies (session, auth) are always on and are not part of the
 * consent decision. Analytics cookies (PostHog) only load if the user clicks
 * "Accept all".
 */
export default function CookieConsent() {
  const [visible, setVisible] = useState(false);
  const theme = useTheme();
  const brand = theme.palette.primary.main;
  const brandDark = theme.palette.primary.dark;

  useEffect(() => {
    // Show only if no consent recorded yet
    setVisible(getConsent() === null);
  }, []);

  if (!visible) return null;

  const decide = (analytics: boolean) => {
    setConsent(analytics);
    setVisible(false);
  };

  return (
    <Box
      role="dialog"
      aria-label="Cookie preferences"
      aria-modal="false"
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: (t) => t.zIndex.modal + 10,
        bgcolor: COOKIE_CONSENT_COLORS.bg,
        color: COOKIE_CONSENT_COLORS.fg,
        boxShadow: '0 -4px 20px rgba(0,0,0,0.2)',
      }}
    >
      <Box
        sx={{
          maxWidth: 1200,
          mx: 'auto',
          px: { xs: 2, sm: 3 },
          py: 2,
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'flex-start', md: 'center' },
          gap: { xs: 1.5, md: 3 },
        }}
      >
        <Box sx={{ flex: 1 }}>
          <Typography
            sx={{ fontSize: TYPE.sm, fontWeight: TYPE.semibold, mb: 0.5 }}
          >
            We value your privacy
          </Typography>
          <Typography
            sx={{ fontSize: TYPE.xs, color: COOKIE_CONSENT_COLORS.mutedFg, lineHeight: 1.5 }}
          >
            We use essential cookies to keep you signed in and to operate the service.
            With your permission we also use analytics cookies to understand how the
            product is used so we can improve it. You can change this any time in
            Settings.{' '}
            <MuiLink
              href="/privacy"
              target="_blank"
              rel="noopener noreferrer"
              sx={{ color: COOKIE_CONSENT_COLORS.linkFg, textDecoration: 'underline' }}
            >
              Privacy policy
            </MuiLink>
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'flex',
            gap: 1,
            flexShrink: 0,
            width: { xs: '100%', md: 'auto' },
          }}
        >
          <Button
            variant="outlined"
            onClick={() => decide(false)}
            sx={{
              color: COOKIE_CONSENT_COLORS.fg,
              borderColor: COOKIE_CONSENT_COLORS.outlineBorder,
              fontSize: TYPE.xs,
              textTransform: 'none',
              flex: { xs: 1, md: 'none' },
              '&:hover': { borderColor: COOKIE_CONSENT_COLORS.fg, bgcolor: 'rgba(255,255,255,0.06)' },
            }}
          >
            Essential only
          </Button>
          <Button
            variant="contained"
            onClick={() => decide(true)}
            sx={{
              bgcolor: brand,
              color: COOKIE_CONSENT_COLORS.fg,
              fontSize: TYPE.xs,
              textTransform: 'none',
              boxShadow: 'none',
              flex: { xs: 1, md: 'none' },
              '&:hover': { bgcolor: brandDark, boxShadow: 'none' },
            }}
          >
            Accept all
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
